"use client";

import { Eye, MessageSquare, Users, FileText, CircleCheck as CheckCircle2 } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const metrics = [{
  icon: Eye,
  value: "35.3K+",
  label: "Facebook Views",
  growth: "↑ 968% Increase",
  graph: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1712154/img/graph_facebook.png"
}, {
  icon: MessageSquare,
  value: "2.0K+",
  label: "Content Interactions",
  growth: "↑ 33,000% Increase",
  graph: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1712154/img/graph_interactions.png"
}, {
  icon: Users,
  value: "60+",
  label: "New Followers",
  growth: "↑ 1,100% Increase",
  graph: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1712154/img/graph_followers.webp"
}, {
  icon: FileText,
  value: "200+",
  label: "Content Pieces Created",
  growth: "In 7+ Months",
  graph: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1712154/img/graph_content.png"
}];
const tags = ["Content Strategy", "Graphic Design", "Video Editing", "Community Engagement", "Performance Tracking"];
export default function Growth() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="9518" id="growth-metrics" className="bg-muted/30 py-12 md:py-16">
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-12">
        <div ref={headerRef} className={`text-center mb-10 transition-all duration-800 ease-[cubic-bezier(0.16,1,0.3,1)] ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <p className="font-mono text-[0.75rem] font-medium tracking-[0.2em] uppercase text-primary mb-4">
            MY IMPACT AT
          </p>
          <h2 className="font-default font-bold text-foreground leading-tight tracking-tight text-[clamp(2rem,5vw,3.5rem)]"><span className="text-primary">PENTA</span> Technology Solutions (Organic)</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {metrics.map((metric, i) => <MetricCard key={metric.label} index={i} {...metric} />)}
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-4">
          {tags.map((tag, i) => <div key={tag} className="flex items-center gap-2 text-foreground/80 font-medium" style={{
          transitionDelay: `${i * 100}ms`
        }}>
              <CheckCircle2 className="size-5 text-primary" />
              <span className="text-sm md:text-base">{tag}</span>
            </div>)}
        </div>
      </div>
    </section>;
}
function MetricCard({
  index,
  icon: Icon,
  value,
  label,
  growth,
  graph
}: {
  index: number;
  icon: any;
  value: string;
  label: string;
  growth: string;
  graph: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} data-index={index} style={{
    transitionDelay: isVisible ? `${index * 100}ms` : "0ms"
  }} className={`group relative bg-card border border-border p-6 md:p-8 flex flex-col transition-all duration-800 ease-[cubic-bezier(0.16,1,0.3,1)] rounded-2xl shadow-sm hover:shadow-md ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <div className="flex items-start gap-5">
        <div className="flex-shrink-0 size-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon className="size-6 text-primary" strokeWidth={1.5} />
        </div>
        <div className="flex-grow">
          <div className="flex flex-col">
            <span className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">
              {value}
            </span>
            <span className="text-muted-foreground font-medium text-sm md:text-base mt-1">
              {label}
            </span>
            <span className={`text-xs md:text-sm font-semibold mt-2 ${growth.includes("Increase") ? "text-emerald-500" : "text-primary"}`}>
              {growth}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-8 w-full h-16 overflow-hidden">
        <img src={graph} data-wvc-srcset={`${graph}-wvc-srcset`} data-wvc-sizes={`${graph}-wvc-sizes`} alt={`${label} growth chart`} className="w-full h-full object-contain opacity-60 group-hover:opacity-100 transition-opacity duration-300" loading="lazy" />
      </div>
    </div>;
}